import { useLanguage } from '../context/LanguageContext';
import { PreSaleBanner } from '../components/layout/PreSaleBanner';
import { Navbar } from '../components/layout/Navbar';
import { Footer } from '../components/layout/Footer';
import { SEO } from '../components/SEO';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { FlowBackground } from '../components/home/FlowBackground';
import { PAGE_BG, GLASS, PAGE_TITLE } from '../components/home/glass';

const PARAGRAPHS_EN = [
    {
        kind: 'p',
        text: 'Sleepstack was born out of frustration with supplements where the story is often bigger than the explanation behind it. We wanted to know: why is this ingredient in here, why this amount, and how well does that choice actually match the research?',
    },
    {
        kind: 'p',
        text: 'So we do it differently. We show the full formula, use published research as our starting point, and explain the trade-offs we make going from research to final product.',
    },
    {
        kind: 'p',
        text: 'Sometimes a dose lines up directly with human studies. Sometimes, for example with plant extracts, form and standardisation also play a role. We don\'t pretend those situations are the same.',
    },
    {
        kind: 'p',
        text: 'No big sleep promises. Just a formula we can explain as well as we possibly can.',
    },
    {
        kind: 'p',
        text: 'What\'s in it, how much you get, and where our choices come from: that\'s what we\'ll tell you. The rest is up to you.',
    },
];

const PARAGRAPHS_NL = [
    {
        kind: 'p',
        text: 'Sleepstack ontstond uit frustratie over supplementen waarbij het verhaal vaak groter is dan de uitleg erachter. Wij wilden weten: waarom zit dit ingrediënt erin, waarom deze hoeveelheid en hoe goed sluit die keuze eigenlijk aan bij het onderzoek?',
    },
    {
        kind: 'p',
        text: 'Daarom doen we het anders. We laten de volledige formule zien, gebruiken gepubliceerd onderzoek als uitgangspunt en leggen uit welke afwegingen we maken van onderzoek naar eindproduct.',
    },
    {
        kind: 'p',
        text: 'Soms sluit een dosering direct aan op humane studies. Soms, bijvoorbeeld bij plantenextracten, spelen ook vorm en standaardisatie een rol. We doen niet alsof die situaties hetzelfde zijn.',
    },
    {
        kind: 'p',
        text: 'Geen grote slaapbeloftes. Wel een formule die we zo goed mogelijk kunnen uitleggen.',
    },
    {
        kind: 'p',
        text: 'Wat erin zit, hoeveel je krijgt en waar onze keuzes vandaan komen, vertellen we je gewoon. De rest is aan jou.',
    },
];

const PRINCIPLES = [
    {
        num: '01',
        title_en: 'No outcome promises',
        title_nl: 'Geen beloftes over resultaat',
        desc_en: 'We don\'t tell you how you\'ll sleep. We show what\'s in the formula, why we chose it, and which research is relevant. No guarantees, just explanation.',
        desc_nl: 'We vertellen je niet hoe je zult slapen. We laten zien wat er in de formule zit, waarom we het hebben gekozen en welk onderzoek daarbij relevant is. Geen garanties, wel uitleg.',
    },
    {
        num: '02',
        title_en: 'Transparency goes beyond the label',
        title_nl: 'Transparantie gaat verder dan het etiket',
        desc_en: 'Making ingredients and amounts visible is the baseline. We also explain why we use them, how we assess the research, and where the sale price actually goes.',
        desc_nl: 'Ingrediënten en hoeveelheden zichtbaar maken is de basis. We leggen ook uit waarom we ze gebruiken, hoe we het onderzoek beoordelen en waar de verkoopprijs naartoe gaat.',
        link: '/business',
        link_en: 'See the cost breakdown',
        link_nl: 'Bekijk de kostenopbouw',
    },
    {
        num: '03',
        title_en: 'Research is the starting point, not the recipe',
        title_nl: 'Onderzoek is het uitgangspunt, niet het recept',
        desc_en: 'We start from published human research and then look at dosage, form and standardisation. Sometimes that lines up one-to-one. Sometimes a raw material needs more context. Every amount needs a reason.',
        desc_nl: 'We beginnen bij gepubliceerd humaan onderzoek en kijken daarna naar dosering, vorm en standaardisatie. Soms sluit dat één-op-één aan. Soms vraagt een grondstof om meer context. Elke hoeveelheid moet een reden hebben.',
    },
    {
        num: '04',
        title_en: 'Every ingredient needs a reason to be there',
        title_nl: 'Elk ingrediënt moet een reden hebben om erin te zitten',
        desc_en: 'That includes supporting ingredients. Natural cherry flavour, beetroot extract, citric acid and chicory inulin each have a clear function. No sucralose, no additives that raise unnecessary questions in a formula built for nightly use.',
        desc_nl: 'Dat geldt ook voor hulpstoffen. Natuurlijk kersenaroma, bietenrood-extract, citroenzuur en cichorei-inuline hebben elk een duidelijke functie. Geen sucralose, geen toevoegingen die onnodige vragen oproepen in een formule voor dagelijks avondgebruik.',
    },
];

export default function About() {
    const { t, lang } = useLanguage();
    const paragraphs = lang === 'nl' ? PARAGRAPHS_NL : PARAGRAPHS_EN;

    return (
        <div>
            <SEO
                title="About Sleepstack"
                description="Designed in the Netherlands, manufactured in an EU GMP-certified facility. The story, formula principles, and team behind Sleepstack."
                path="/about"
            />
            <PreSaleBanner />
            <Navbar />
            <main className="relative isolate glass-scope" style={{ paddingTop: 'var(--page-offset, 128px)', background: PAGE_BG }}>
                <FlowBackground variant="about" />
                {/* Hero */}
                <section className="section-padding bg-white">
                    <div className="section-container">
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.5 }}
                            className={`max-w-3xl mx-auto p-6 sm:p-8 lg:p-10 ${GLASS}`}
                        >
                            <h1
                                className={PAGE_TITLE}
                                data-testid="about-title"
                            >
                                {lang === 'nl' ? (
                                    <>
                                        Sleepstack belooft je geen<br />
                                        <span className="text-gradient-blue">betere slaap.</span>
                                    </>
                                ) : (
                                    <>
                                        Sleepstack doesn&apos;t promise<br />
                                        <span className="text-gradient-blue">you better sleep.</span>
                                    </>
                                )}
                            </h1>
                        </motion.div>
                    </div>
                </section>

                {/* Story */}
                <section className="pb-20 -mt-6 md:-mt-10">
                    <div className={`section-container max-w-3xl space-y-6 p-6 sm:p-8 lg:p-10 mx-4 sm:mx-auto ${GLASS}`} data-testid="about-story-panel">
                        {paragraphs.map((p, i) => (
                            <motion.div
                                key={i}
                                initial={{ opacity: 0, y: 16 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true, margin: '-80px' }}
                                transition={{ duration: 0.45, delay: i * 0.05 }}
                            >
                                {p.kind === 'pullquote' ? (
                                    <blockquote
                                        className="border-l-4 border-primary pl-6 py-2 my-4"
                                        data-testid={`about-pullquote-${i}`}
                                    >
                                        <p className="font-heading text-2xl md:text-3xl font-black text-foreground leading-tight tracking-tight">
                                            &ldquo;{p.text}&rdquo;
                                        </p>
                                    </blockquote>
                                ) : (
                                    <p className="text-base md:text-lg text-slate-800 leading-relaxed">
                                        {p.text}
                                    </p>
                                )}
                            </motion.div>
                        ))}
                    </div>
                </section>

                {/* Formula principles */}
                <section className="bg-accent py-20">
                    <div className="section-container max-w-5xl">
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.5 }}
                            className="mb-10 max-w-2xl"
                        >
                            <h2 className="heading-lg text-foreground mb-3">
                                {lang === 'nl' ? 'Vier regels die we niet doorbreken.' : 'Four rules we don\'t break.'}
                            </h2>
                        </motion.div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            {PRINCIPLES.map((p, i) => (
                                <motion.div
                                    key={p.num}
                                    initial={{ opacity: 0, y: 20 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    viewport={{ once: true }}
                                    transition={{ duration: 0.45, delay: i * 0.08 }}
                                    className="bg-white border border-border rounded-2xl p-8 hover:border-primary/40 transition-colors"
                                    data-testid={`principle-${p.num}`}
                                >
                                    <div className="font-condensed text-6xl font-bold text-primary/15 leading-none mb-4">
                                        {p.num}
                                    </div>
                                    <h3 className="font-heading text-xl text-foreground mb-3 leading-tight">
                                        {lang === 'nl' ? p.title_nl : p.title_en}
                                    </h3>
                                    <p className="text-sm text-slate-700 leading-relaxed">
                                        {lang === 'nl' ? p.desc_nl : p.desc_en}
                                    </p>
                                    {p.link && (
                                        <Link
                                            to={p.link}
                                            className="inline-flex items-center gap-1.5 text-primary text-sm font-semibold mt-4 hover:underline"
                                            data-testid={`principle-link-${p.num}`}
                                        >
                                            {lang === 'nl' ? p.link_nl : p.link_en}
                                            <ArrowRight size={14} />
                                        </Link>
                                    )}
                                </motion.div>
                            ))}
                        </div>
                    </div>
                </section>

                {/* Contact */}
                <section className="bg-accent py-20">
                    <div className={`section-container max-w-xl p-6 sm:p-8 lg:p-10 mx-4 sm:mx-auto ${GLASS}`} data-testid="about-contact-panel">
                        <h2 className="heading-lg text-foreground mb-6">
                            {lang === 'nl' ? 'Contact' : 'Contact'}
                        </h2>
                        <div className="space-y-2 text-sm text-slate-700">
                            <p>
                                Email:{' '}
                                <a href="mailto:info@sleepstack.nl" className="text-primary hover:underline font-semibold">
                                    info@sleepstack.nl
                                </a>
                            </p>
                            <p>KVK: 88291308</p>
                            <p>BTW: NL230290541B02</p>
                            <p>{lang === 'nl' ? 'Geregistreerd in Nederland' : 'Registered in the Netherlands'}</p>
                        </div>
                    </div>
                </section>
            </main>
            <Footer />
        </div>
    );
}
